/**
 * Dinheiro em centavos inteiros.
 *
 * O arquivo de dados guarda reais (`249.9`) porque é o que o cliente consegue
 * editar sem errar. Passando desse limite, todo cálculo acontece em centavos
 * inteiros: `0.1 + 0.2 !== 0.3` em float, e somar `3.90` cinquenta e uma vezes
 * já produz `198.90000000000018` — o suficiente para o carrinho discordar do
 * total exibido na tela.
 *
 * É também o formato que gateways de pagamento esperam: o `unit_amount` do
 * Stripe é um inteiro em centavos.
 */

/** Valor monetário em centavos. Sempre inteiro. */
export type Cents = number;

/** `249.9` → `24990`. Ponto de entrada de todo valor vindo do catálogo. */
export function toCents(reais: number): Cents {
  return Math.round(reais * 100);
}

/** `24990` → `249.9`. Use apenas na fronteira com APIs que exigem decimal. */
export function fromCents(cents: Cents): number {
  return cents / 100;
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** `24990` → `"R$ 249,90"` */
export function formatCents(cents: Cents): string {
  return BRL.format(fromCents(cents));
}

/** Soma segura — mantém o resultado inteiro. */
export function sumCents(values: Cents[]): Cents {
  return values.reduce((total, value) => total + value, 0);
}

/** `24990`, `1.5` → `"R$ 166,60/kg"` */
export function formatCentsPerKg(cents: Cents, weightKg: number): string {
  if (weightKg <= 0) return '';
  return `${formatCents(Math.round(cents / weightKg))}/kg`;
}

/**
 * Percentual de desconto entre o preço cheio e o praticado.
 * Retorna `null` quando não há oferta válida.
 */
export function discountRatio(priceCents: Cents, compareAtCents?: Cents): number | null {
  if (!compareAtCents || compareAtCents <= priceCents) return null;
  return (compareAtCents - priceCents) / compareAtCents;
}

/**
 * Converte o que o cliente digitou no campo de troco para centavos.
 * Aceita "R$ 1.000,00", "1000", "1000,50". Retorna `null` se não der para ler.
 */
export function parseCurrencyBRToCents(value: string): Cents | null {
  const cleaned = value
    .replace(/[^\d.,]/g, '')
    .replace(/\.(?=\d{3}\b)/g, '') // separador de milhar
    .replace(',', '.');

  if (cleaned === '') return null;

  const amount = Number(cleaned);
  return Number.isFinite(amount) ? toCents(amount) : null;
}
